<template>
  <section id="contact" class="contact-section">
    <div class="container">
      <div class="row g-5 align-items-start">

        <div class="col-lg-5">
          <span class="section-label" v-reveal>{{ $t('contact.label') }}</span>
          <h2 class="section-title" v-reveal="80">{{ $t('contact.title') }}</h2>
          <p class="contact-intro" v-reveal="140">{{ $t('contact.intro') }}</p>

          <div class="contact-info-list" v-reveal="200">
            <div class="contact-info-item">
              <div class="contact-info-icon"><i class="fas fa-envelope"></i></div>
              <div>
                <span class="contact-info-label">{{ $t('contact.email_label') }}</span>
                <span class="contact-info-value">info@pixelated-it.com</span>
              </div>
            </div>
            <div class="contact-info-item">
              <div class="contact-info-icon"><i class="fas fa-clock"></i></div>
              <div>
                <span class="contact-info-label">{{ $t('contact.response_label') }}</span>
                <span class="contact-info-value">{{ $t('contact.response_value') }}</span>
              </div>
            </div>
            <div class="contact-info-item">
              <div class="contact-info-icon"><i class="fas fa-map-marker-alt"></i></div>
              <div>
                <span class="contact-info-label">{{ $t('contact.location_label') }}</span>
                <span class="contact-info-value">{{ $t('contact.location_value') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-7" v-reveal="100">
          <div class="contact-form-card">
            <form @submit.prevent="handleSubmit">

              <div class="row g-3">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="naam">{{ $t('contact.form_name') }}</label>
                    <input id="naam" v-model="form.naam" type="text" class="form-control-input"
                      :placeholder="$t('contact.form_name_placeholder')" required />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="email">{{ $t('contact.form_email') }}</label>
                    <input id="email" v-model="form.email" type="email" class="form-control-input"
                      placeholder="hello@example.com" required />
                  </div>
                </div>
              </div>

              <div class="row g-3">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="type">{{ $t('contact.form_type') }}</label>
                    <select id="type" v-model="form.type" class="form-control-input form-select-custom" required>
                      <option value="" disabled>{{ $t('contact.form_type_placeholder') }}</option>
                      <option value="mvp">{{ $t('contact.form_type_mvp') }}</option>
                      <option value="software-uitbreiding">{{ $t('contact.form_type_solution') }}</option>
                      <option value="app">{{ $t('contact.form_type_app') }}</option>
                      <option value="website">{{ $t('contact.form_type_website') }}</option>
                      <option value="anders">{{ $t('contact.form_type_other') }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="budget">{{ $t('contact.form_budget') }}</label>
                    <select id="budget" v-model="form.budget" class="form-control-input form-select-custom" required>
                      <option value="" disabled>{{ $t('contact.form_budget_placeholder') }}</option>
                      <option value="< €1.000">&#60; €1.000</option>
                      <option value="€1.000 – €5.000">€1.000 – €5.000</option>
                      <option value="€5.000 – €10.000">€5.000 – €10.000</option>
                      <option value="> €10.000">&#62; €10.000</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label for="bericht">{{ $t('contact.form_message') }}</label>
                <textarea id="bericht" v-model="form.bericht"
                  class="form-control-input form-control-textarea"
                  :placeholder="$t('contact.form_message_placeholder')" required></textarea>
              </div>

              <button type="submit" class="btn-submit" :disabled="status === 'sending'">
                <template v-if="status === 'sending'">
                  <i class="fas fa-spinner fa-spin"></i> {{ $t('contact.sending') }}
                </template>
                <template v-else>
                  {{ $t('contact.form_submit') }} <i class="fas fa-paper-plane"></i>
                </template>
              </button>

              <div v-if="status === 'success'" class="form-feedback success">
                <i class="fas fa-check-circle"></i>
                {{ $t('contact.success') }}
              </div>
              <div v-if="status === 'error'" class="form-feedback error">
                <i class="fas fa-exclamation-circle"></i>
                {{ $t('contact.error') }}
              </div>

            </form>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script>
import emailjs from '@emailjs/browser'

const SERVICE_ID  = process.env.VUE_APP_EMAILJS_SERVICE_ID
const TEMPLATE_ID = process.env.VUE_APP_EMAILJS_TEMPLATE_ID
const PUBLIC_KEY  = process.env.VUE_APP_EMAILJS_PUBLIC_KEY

export default {
  name: "ContactComponent",
  data() {
    return {
      form: { naam: '', email: '', type: '', budget: '', bericht: '' },
      status: null, // null | 'sending' | 'success' | 'error'
    }
  },
  methods: {
    async handleSubmit() {
      this.status = 'sending'
      try {
        await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
          from_name:    this.form.naam,
          from_email:   this.form.email,
          project_type: this.form.type,
          budget:       this.form.budget,
          message:      this.form.bericht,
        }, PUBLIC_KEY)

        this.status = 'success'
        this.form = { naam: '', email: '', type: '', budget: '', bericht: '' }
      } catch (err) {
        console.error('EmailJS error:', err)
        this.status = 'error'
      }
    },
  },
}
</script>

<style scoped>
.contact-section {
  padding: 6.5rem 0 5.5rem;
  background-color: transparent;
  position: relative;
}

.section-label {
  display: inline-block;
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.8rem;
  color: var(--accent);
  letter-spacing: 1px;
  margin-bottom: 0.875rem;
}

.section-title {
  font-family: 'Jersey 15', sans-serif;
  font-size: 2.75rem;
  color: var(--text);
  font-weight: 400;
  margin-bottom: 1.25rem;
  letter-spacing: 0;
}

.contact-intro {
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 2.25rem;
}

.contact-info-list { display: flex; flex-direction: column; gap: 1.25rem; }

.contact-info-item { display: flex; align-items: flex-start; gap: 1rem; }

.contact-info-icon {
  width: 42px;
  height: 42px;
  background: var(--accent-tint);
  border: 1px solid var(--accent-line);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  font-size: 0.95rem;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
}

.contact-info-item:hover .contact-info-icon {
  background: rgba(250, 168, 43, 0.2);
  transform: scale(1.05);
  box-shadow: 0 0 24px var(--accent-tint);
}

.contact-info-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-dim);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.15rem;
}

.contact-info-value { display: block; font-size: 0.95rem; color: var(--text); font-weight: 500; }

.contact-form-card {
  background: linear-gradient(180deg, #151515, #0e0e0e);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 2.25rem 2.5rem;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.form-group { margin-bottom: 0; }

.form-group label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-soft);
  margin-bottom: 0.4rem;
}

.form-control-input {
  width: 100%;
  padding: 0.8rem 1rem;
  border: 1px solid var(--border-strong);
  border-radius: 10px;
  background-color: var(--bg);
  font-size: 0.9rem;
  color: var(--text);
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  -webkit-appearance: none;
}

.form-control-input:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-tint);
  outline: none;
  background-color: var(--surface);
}

.form-control-input::placeholder { color: var(--text-dim); }
.form-control-textarea { height: 10rem; resize: vertical; }

.form-control-input option { background: var(--surface-2); color: var(--text); }

.form-select-custom {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23FAA82B' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.875rem center;
  background-size: 14px;
  padding-right: 2.5rem;
}

.btn-submit {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  justify-content: center;
  margin-top: 0.5rem;
  padding: 0.9rem 2rem;
  background: var(--accent-grad);
  color: #0a0a0a;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 6px 24px var(--accent-glow);
}

.btn-submit:hover {
  filter: brightness(1.06);
  transform: translateY(-1px);
  box-shadow: 0 10px 34px var(--accent-glow);
}

.btn-submit i { font-size: 0.85rem; }

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.form-feedback {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
}

.form-feedback.success {
  background: rgba(52, 211, 153, 0.1);
  border: 1px solid rgba(52, 211, 153, 0.35);
  color: #6ee7b7;
}

.form-feedback.error {
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid rgba(248, 113, 113, 0.35);
  color: #fca5a5;
}

@media (max-width: 991px) { .contact-section { padding: 5rem 0 4rem; } }
@media (max-width: 767px) {
  .contact-section { padding: 4rem 0 3.5rem; }
  .section-title { font-size: 1.875rem; }
  .contact-intro { font-size: 0.95rem; }
  .contact-form-card { padding: 1.5rem 1.25rem; }
  .btn-submit { padding: 0.875rem 1.5rem; font-size: 0.9rem; }
}
</style>
